import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTarget,
  FiCheck,
  FiArrowRight,
  FiAward,
  FiTrendingUp,
  FiX,
  FiSearch,
} from 'react-icons/fi';
import Sidebar from '../../Sidebar';
import './TAGESchoolSelection.css';
import tageSchoolsData from './tageSchools.json';

const TAGESchoolSelection = () => {
  const navigate = useNavigate();
  const [selectedSchools, setSelectedSchools] = useState({
    top1: null,
    top2: null,
    top3: null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [currentSelection, setCurrentSelection] = useState('top1'); // Which slot is being filled
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state

  const schools = tageSchoolsData.schools;

  // Load saved selections and mark workflow as started
  useEffect(() => {
    const savedSelections = localStorage.getItem('tage_school_selections');
    if (savedSelections) {
      setSelectedSchools(JSON.parse(savedSelections));
    }

    // Mark that user has started the TAGE MAGE workflow
    localStorage.setItem('tage_workflow_started', 'true');
  }, []);

  // Save selections
  const saveSelections = (selections) => {
    localStorage.setItem('tage_school_selections', JSON.stringify(selections));
  };

  // Filter schools
  const filteredSchools = schools.filter((school) => {
    const matchesSearch =
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.program.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === 'all' || school.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Open modal for selecting a school
  const openSchoolModal = (selectionSlot) => {
    setCurrentSelection(selectionSlot);
    setIsModalOpen(true);
    setSearchTerm('');
    setFilterCategory('all');
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Handle school selection
  const handleSelectSchool = (school) => {
    const newSelections = {
      ...selectedSchools,
      [currentSelection]: school,
    };
    setSelectedSchools(newSelections);
    saveSelections(newSelections);

    // Close modal
    setIsModalOpen(false);

    // Move to next selection slot if empty
    if (currentSelection === 'top1' && !selectedSchools.top2) {
      setCurrentSelection('top2');
    } else if (currentSelection === 'top2' && !selectedSchools.top3) {
      setCurrentSelection('top3');
    }
  };

  // Handle continue
  const handleContinue = () => {
    if (selectedSchools.top1) {
      // Navigate to diagnostic test
      navigate('/exams/tage/diagnostic');
    }
  };

  // Get category label
  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case 'Grande École':
        return 'schoolselection-category-grande-ecole';
      case 'IAE':
        return 'schoolselection-category-iae';
      case 'Mastère Spécialisé':
        return 'schoolselection-category-mastere';
      default:
        return 'schoolselection-category-default';
    }
  };

  // Get score level color
  const getScoreLevelColor = (score) => {
    if (score >= 400) return 'schoolselection-score-excellent';
    if (score >= 350) return 'schoolselection-score-tres-bon';
    if (score >= 300) return 'schoolselection-score-bon';
    if (score >= 250) return 'schoolselection-score-moyen';
    return 'schoolselection-score-faible';
  };

  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <div className="schoolselection-container">
          {/* Header */}
          <div className="schoolselection-header">
            <div className="schoolselection-header-icon">
              <FiAward size={40} />
            </div>
            <h1>Choisissez vos écoles cibles</h1>
            <p className="schoolselection-header-subtitle">
              Sélectionnez jusqu'à 3 programmes pour personnaliser votre
              préparation au TAGE MAGE. Nous créerons un plan d'étude adapté à
              vos objectifs.
            </p>
          </div>

          {/* Current Selections */}
          <div className="schoolselection-current-selections">
            <h3>
              <FiTarget size={20} /> Vos Choix
            </h3>
            <div className="schoolselection-selections-grid">
              {/* Top 1 */}
              <div
                className={`schoolselection-selection-card ${currentSelection === 'top1' ? 'active' : ''} ${selectedSchools.top1 ? 'selected' : ''}`}
                onClick={() => openSchoolModal('top1')}
              >
                <div className="schoolselection-selection-number">
                  <FiAward size={24} />
                  <span>Top 1</span>
                </div>
                {selectedSchools.top1 ? (
                  <div className="schoolselection-selected-school">
                    <h4>{selectedSchools.top1.name}</h4>
                    <p>{selectedSchools.top1.program}</p>
                    <div className="schoolselection-school-scores">
                      <div className="schoolselection-score-item">
                        <span className="schoolselection-score-label">
                          Score Min
                        </span>
                        <span
                          className={`schoolselection-score-value ${getScoreLevelColor(selectedSchools.top1.minimumScore)}`}
                        >
                          {selectedSchools.top1.minimumScore}
                        </span>
                      </div>
                      <div className="schoolselection-score-item">
                        <span className="schoolselection-score-label">
                          Score Conseillé
                        </span>
                        <span
                          className={`schoolselection-score-value ${getScoreLevelColor(selectedSchools.top1.recommendedScore)}`}
                        >
                          {selectedSchools.top1.recommendedScore}
                        </span>
                      </div>
                    </div>
                    <button
                      className="schoolselection-btn-change"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSchoolModal('top1');
                      }}
                    >
                      Modifier
                    </button>
                  </div>
                ) : (
                  <div className="schoolselection-empty-selection">
                    <p>Sélectionnez votre premier choix</p>
                  </div>
                )}
              </div>

              {/* Top 2 */}
              <div
                className={`schoolselection-selection-card ${currentSelection === 'top2' ? 'active' : ''} ${selectedSchools.top2 ? 'selected' : ''}`}
                onClick={() => openSchoolModal('top2')}
              >
                <div className="schoolselection-selection-number">
                  <FiTrendingUp size={24} />
                  <span>Top 2</span>
                </div>
                {selectedSchools.top2 ? (
                  <div className="schoolselection-selected-school">
                    <h4>{selectedSchools.top2.name}</h4>
                    <p>{selectedSchools.top2.program}</p>
                    <div className="schoolselection-school-scores">
                      <div className="schoolselection-score-item">
                        <span className="schoolselection-score-label">
                          Score Min
                        </span>
                        <span
                          className={`schoolselection-score-value ${getScoreLevelColor(selectedSchools.top2.minimumScore)}`}
                        >
                          {selectedSchools.top2.minimumScore}
                        </span>
                      </div>
                      <div className="schoolselection-score-item">
                        <span className="schoolselection-score-label">
                          Score Conseillé
                        </span>
                        <span
                          className={`schoolselection-score-value ${getScoreLevelColor(selectedSchools.top2.recommendedScore)}`}
                        >
                          {selectedSchools.top2.recommendedScore}
                        </span>
                      </div>
                    </div>
                    <button
                      className="schoolselection-btn-change"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSchoolModal('top2');
                      }}
                    >
                      Modifier
                    </button>
                  </div>
                ) : (
                  <div className="schoolselection-empty-selection">
                    <p>Sélectionnez votre deuxième choix (optionnel)</p>
                  </div>
                )}
              </div>

              {/* Top 3 */}
              <div
                className={`schoolselection-selection-card ${currentSelection === 'top3' ? 'active' : ''} ${selectedSchools.top3 ? 'selected' : ''}`}
                onClick={() => openSchoolModal('top3')}
              >
                <div className="schoolselection-selection-number">
                  <FiCheck size={24} />
                  <span>Top 3</span>
                </div>
                {selectedSchools.top3 ? (
                  <div className="schoolselection-selected-school">
                    <h4>{selectedSchools.top3.name}</h4>
                    <p>{selectedSchools.top3.program}</p>
                    <div className="schoolselection-school-scores">
                      <div className="schoolselection-score-item">
                        <span className="schoolselection-score-label">
                          Score Min
                        </span>
                        <span
                          className={`schoolselection-score-value ${getScoreLevelColor(selectedSchools.top3.minimumScore)}`}
                        >
                          {selectedSchools.top3.minimumScore}
                        </span>
                      </div>
                      <div className="schoolselection-score-item">
                        <span className="schoolselection-score-label">
                          Score Conseillé
                        </span>
                        <span
                          className={`schoolselection-score-value ${getScoreLevelColor(selectedSchools.top3.recommendedScore)}`}
                        >
                          {selectedSchools.top3.recommendedScore}
                        </span>
                      </div>
                    </div>
                    <button
                      className="schoolselection-btn-change"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSchoolModal('top3');
                      }}
                    >
                      Modifier
                    </button>
                  </div>
                ) : (
                  <div className="schoolselection-empty-selection">
                    <p>Sélectionnez votre troisième choix (optionnel)</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="schoolselection-actions">
            <button
              className="btn-lwz-primary schoolselection-btn-continue"
              onClick={handleContinue}
              disabled={!selectedSchools.top1}
            >
              Continuer vers le test diagnostic
              <FiArrowRight size={20} />
            </button>
            {!selectedSchools.top1 && (
              <p className="schoolselection-hint-text">
                Veuillez sélectionner au moins votre Top 1
              </p>
            )}
          </div>
        </div>
      </div>

      {/* School Selection Modal */}
      {isModalOpen && (
        <div className="schoolselection-modal-overlay" onClick={closeModal}>
          <div
            className="schoolselection-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="schoolselection-modal-header">
              <div>
                <h2>
                  <FiSearch size={24} /> Sélectionner une école
                </h2>
                <p className="schoolselection-modal-subtitle">
                  Choisissez pour{' '}
                  <strong>
                    {currentSelection === 'top1'
                      ? 'Top 1'
                      : currentSelection === 'top2'
                        ? 'Top 2'
                        : 'Top 3'}
                  </strong>
                </p>
              </div>
              <button
                className="schoolselection-modal-close"
                onClick={closeModal}
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Filters */}
            <div className="schoolselection-modal-filters">
              <input
                type="text"
                className="schoolselection-search-input"
                placeholder="Rechercher une école..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="schoolselection-category-filters">
                <button
                  className={`schoolselection-filter-btn ${filterCategory === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterCategory('all')}
                >
                  Toutes
                </button>
                <button
                  className={`schoolselection-filter-btn ${filterCategory === 'Grande École' ? 'active' : ''}`}
                  onClick={() => setFilterCategory('Grande École')}
                >
                  Grandes Écoles
                </button>
                <button
                  className={`schoolselection-filter-btn ${filterCategory === 'IAE' ? 'active' : ''}`}
                  onClick={() => setFilterCategory('IAE')}
                >
                  IAE
                </button>
                <button
                  className={`schoolselection-filter-btn ${filterCategory === 'Mastère Spécialisé' ? 'active' : ''}`}
                  onClick={() => setFilterCategory('Mastère Spécialisé')}
                >
                  Mastères Spécialisés
                </button>
              </div>
            </div>

            {/* Schools Grid */}
            <div className="schoolselection-modal-schools-grid">
              {filteredSchools.map((school) => (
                <div
                  key={school.id}
                  className="schoolselection-school-card"
                  onClick={() => handleSelectSchool(school)}
                >
                  <div className="schoolselection-school-card-header">
                    <h4>{school.name}</h4>
                    <span
                      className={`schoolselection-category-badge ${getCategoryBadgeColor(school.category)}`}
                    >
                      {school.category}
                    </span>
                  </div>
                  <p className="schoolselection-school-program">
                    {school.program}
                  </p>
                  <p className="schoolselection-school-description">
                    {school.description}
                  </p>
                  <div className="schoolselection-school-scores-inline">
                    <div className="schoolselection-score-inline">
                      <span className="schoolselection-score-label-inline">
                        Min:
                      </span>
                      <span
                        className={`schoolselection-score-value-inline ${getScoreLevelColor(school.minimumScore)}`}
                      >
                        {school.minimumScore}
                      </span>
                    </div>
                    <div className="schoolselection-score-inline">
                      <span className="schoolselection-score-label-inline">
                        Conseillé:
                      </span>
                      <span
                        className={`schoolselection-score-value-inline ${getScoreLevelColor(school.recommendedScore)}`}
                      >
                        {school.recommendedScore}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TAGESchoolSelection;
